export interface DocEntry {
  slug: string;
  title: string;
  description: string;
  markdown: string;
}

export const DOCS_CONTENT: DocEntry[] = [
  {
    slug: 'introduction',
    title: 'Getting Started with the Wisprs Transcription API',
    description: 'Overview of the Wisprs API, base URL, and quick navigation to key guides.',
    markdown: `# Getting Started with the Wisprs Transcription API

Wisprs is a voice operations API. Submit a URL — YouTube, TikTok, Instagram, podcast, or a direct audio/video file — and get back a timestamped transcript, SRT subtitles, AI-powered repurposings, and semantic search.

The API is REST-based, uses bearer token authentication, and returns JSON. Jobs are async: you submit a URL, receive a job ID immediately, and either poll for status or configure a webhook to be notified on completion.

## Base URL

\`\`\`
https://wisprs.co/api/v1
\`\`\`

## What you can do

- **Ingest any URL** — YouTube, TikTok, Instagram, Spotify, podcast feeds, or any direct audio/video link. No dependency on caption tracks — 100% coverage including Shorts and unlisted content.
- **Async transcription** — submit a job, get a job ID immediately. A webhook fires when the transcript is ready, or poll \`GET /api/v1/jobs/{id}\` for status.
- **Export in any format** — txt, srt, vtt, json, md, or docx.
- **AI repurposing** — summaries, chapter markers, quote extraction, Twitter threads, blog posts, show notes.
- **Semantic library search** — search across all your transcripts by meaning, not just keywords.
- **MCP server** — plug Wisprs into Claude, Cursor, or any MCP-compatible AI host as a voice operations bridge.

## Tiers and quotas

API access is tiered separately from Wisprs app plans. Every account gets the **Free** API tier (no card required): 100 API requests/day at 20 requests/minute, with caption-based transcript reads included and no speech-to-text minutes. Paid API tiers add metered speech-to-text for uncaptioned media: **Build** ($19/mo — 2,000 requests/day, 60 req/min, 300 STT min/mo) and **Scale** ($49/mo — 10,000 requests/day, 200 req/min, 1,200 STT min/mo). Agency and Enterprise app plans include the Scale and Enterprise API tiers respectively. Details and upgrade: [wisprs.co/developer#pricing](https://wisprs.co/developer#pricing).

API speech-to-text minutes are a separate allowance from your app plan's transcription minutes — API-submitted jobs never draw down app minutes.

## Quick navigation

- [Quickstart](https://wisprs.co/developer/docs/quickstart) — Transcribe your first URL in 5 minutes.
- [Authentication](https://wisprs.co/developer/docs/authentication) — API keys, scopes, tiers, quotas, and rate limits.
- [Jobs](https://wisprs.co/developer/docs/jobs) — Async transcription lifecycle.
- [Webhooks](https://wisprs.co/developer/docs/webhooks) — Get notified when jobs complete.
- [Export](https://wisprs.co/developer/docs/export) — SRT, VTT, JSON, Markdown, DOCX.
- [API Reference](https://wisprs.co/developer/api) — Interactive OpenAPI playground.
- [MCP server](https://wisprs.co/developer/docs/mcp) — Connect ChatGPT, Claude or any MCP host.
- CLI — \`npm i -g @wisprs/cli\`, then \`wisprs transcribe "<url>" --wait\`.
- OpenAPI spec — [/openapi.json](https://wisprs.co/openapi.json): every operation with typed responses and per-operation scopes.
- Versioning policy — breaking changes only under a new major prefix; deprecated operations return \`Deprecation\`/\`Sunset\` headers at least 6 months ahead.
`,
  },
  {
    slug: 'quickstart',
    title: 'Quickstart: Transcribe Audio in Under 5 Minutes',
    description: 'Transcribe a YouTube video and export SRT subtitles in under 5 minutes.',
    markdown: `# Quickstart: Transcribe Audio in Under 5 Minutes

The Wisprs API lets you transcribe any audio or video source and export results as SRT, VTT, JSON, or plain text. This guide gets you from zero to your first transcript in under 5 minutes.

## 1. Get an API key

Go to [Dashboard → API Settings](https://wisprs.co/dashboard/settings/api) and create a key. Grant at minimum the scopes \`media:ingest\` and \`transcriptions:read\`.

## 2. Install the SDK (optional)

\`\`\`bash
npm install @wisprs/sdk
# or
pip install wisprs
\`\`\`

## 3. Ingest a URL

Submit a YouTube, TikTok, or direct audio URL. You get a job ID back immediately.

\`\`\`typescript
import { WisprsClient } from '@wisprs/sdk';

const client = new WisprsClient({ apiKey: 'wisprs_sk_your_api_key' });

const job = await client.transcriptions.create({
  source: { url: 'https://youtube.com/watch?v=dQw4w9WgXcQ' },
});

console.log(job.id); // → 42
\`\`\`

\`\`\`bash
# cURL
curl -X POST https://wisprs.co/api/v1/media/ingest \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://youtube.com/watch?v=dQw4w9WgXcQ"}'
\`\`\`

## 4. Wait for the transcript

The SDK's \`jobs.wait()\` polls until the job completes (default 2s interval, 2m timeout).

\`\`\`typescript
const result = await client.jobs.wait(job.id);
console.log(result.transcriptText); // → "Never gonna give you up..."
\`\`\`

Or poll manually with \`GET /api/v1/jobs/{id}\`. The \`status\` field moves from \`pending\` (or \`waiting\`) → \`processing\` → \`completed\` (or \`failed\`).

## 5. Export as SRT

\`\`\`typescript
const srt = await client.transcriptions.export(job.id, { format: 'srt' });
// "1\\n00:00:00,000 --> 00:00:03,240\\nNever gonna give you up\\n\\n2\\n..."
\`\`\`

## Next steps

- [Set up a webhook](https://wisprs.co/developer/docs/webhooks) to avoid polling entirely.
- [Configure scopes](https://wisprs.co/developer/docs/authentication) to restrict what each key can do.
- [Read the Jobs guide](https://wisprs.co/developer/docs/jobs) for quality settings, speaker diarization, and retry logic.
- [Explore the API reference](https://wisprs.co/developer/api) for repurposing, semantic search, and more.

## FAQ

### Is there a free tier?

Yes. The free API tier includes 100 API requests per day at no cost, with no credit card required. Speech-to-text transcription of uncaptioned media requires a paid API tier (Build $19/mo or Scale $49/mo) — see [tiers and quotas](https://wisprs.co/developer/docs/authentication).

### What audio and video formats are supported?

The API accepts public URLs (YouTube, TikTok, Instagram, podcast feeds, direct audio/video links) as well as file uploads. Common formats include MP3, MP4, M4A, WAV, OGG, and WebM.

### How long does transcription take?

Most jobs complete in a fraction of the audio duration. A 10-minute video typically processes in 1–3 minutes. Long-form content (1+ hour) may take proportionally longer during peak load.
`,
  },
  {
    slug: 'authentication',
    title: 'API Key Authentication & Permission Scopes',
    description:
      'Authenticate with the Wisprs API using bearer tokens. Scope-based access control, rate limits, and key rotation.',
    markdown: `# API Key Authentication & Permission Scopes

All Wisprs API requests authenticate with a bearer token. Keys are scoped — each key only carries the permissions you explicitly grant it, so you can limit exposure in production environments.

## Creating a key

Go to [Dashboard → API Settings](https://wisprs.co/dashboard/settings/api). Name your key, choose an optional expiry, and select scopes. The raw key is shown once on creation — store it securely.

## Marketplace jobs

RapidAPI requests are isolated by the proxy-verified \`X-RapidAPI-User\` identity after the proxy secret is checked. Jobs, retries, uploads, and webhook endpoints belong to that subscriber. Legacy marketplace jobs created before subscriber attribution was stored cannot be safely assigned and are not visible through marketplace polling.

Apify actors share a publisher API key, so each submitted job returns a per-job \`jobAccessToken\`. Send it as \`X-Wisprs-Job-Token\` when polling, retrying, exporting, or generating derived content, and store it with the job ID. It is a bearer secret. Apify uses per-job callbacks; shared webhook endpoint management and library search are unavailable on the shared key.

## Using your key

Pass the key as a \`Bearer\` token in every request:

\`\`\`bash
curl https://wisprs.co/api/v1/usage \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

## Scopes

| Scope | What it grants |
|---|---|
| \`media:ingest\` | Submit URLs and upload files for transcription |
| \`transcriptions:write\` | Create transcription jobs |
| \`transcriptions:read\` | Read job status and transcript text |
| \`exports:read\` | Download exports (txt, srt, vtt, json, md, docx) |
| \`repurpose:run\` | Generate summaries, chapters, quotes, blog posts, threads |
| \`library:search\` | Semantic search across your transcript library |
| \`webhooks:manage\` | Create and manage webhook endpoints |
| \`usage:read\` | Read your usage metrics and request logs |
| \`keys:manage\` | Create and revoke API keys (restrict carefully) |

## Tiers, rate limits, and quotas

Any signed-in user can create an API key — the free API tier is real and card-free. Your key's limits come from your API tier, resolved on every request:

| Tier | Price | Requests / day | Requests / min | STT minutes / mo |
|---|---|---|---|---|
| Free | $0 | 100 | 20 | 0 |
| Build | $19/mo | 2,000 | 60 | 300 |
| Scale | $49/mo | 10,000 | 200 | 1,200 |
| Enterprise | Custom | Unlimited | 1,000 | Custom |

- **Requests/day** counts every /api/v1 call, per account (UTC day). Caption-based transcript reads are the cheap path, hence the generous request budgets.
- **STT minutes/mo** meter transcription of uncaptioned media submitted through the API. This allowance is separate from your Wisprs app plan's minutes — API jobs never consume app minutes. On the Free tier, submitting an STT transcription job returns \`402\`.
- Agency app plans include the Scale API tier; Enterprise app plans include the Enterprise API tier.
- Per-minute rate limits apply per key; the daily quota applies per account, so extra keys don't multiply it.

Upgrade at [wisprs.co/developer#pricing](https://wisprs.co/developer#pricing).

## Error responses

All errors share one JSON shape. Quota and rate-limit errors add a machine-readable \`code\` and an \`upgradeUrl\`:

\`\`\`json
{
  "success": false,
  "error": "Daily API request quota exhausted (100 requests/day on the free API tier). The quota resets at UTC midnight.",
  "code": "daily_quota_exceeded",
  "tier": "free",
  "quota": { "limit": 100, "used": 101, "resetInSeconds": 18000 },
  "upgradeUrl": "https://wisprs.co/developer#pricing"
}
\`\`\`

- \`401\` = missing/invalid key
- \`402\` \`code: "api_stt_quota_exceeded"\` = your tier's monthly speech-to-text allowance is exhausted (or the tier includes none)
- \`403\` = missing scope
- \`429\` \`code: "rate_limit_exceeded"\` = per-minute rate limit hit (see \`Retry-After\`)
- \`429\` \`code: "daily_quota_exceeded"\` = daily request quota exhausted (\`Retry-After\` = seconds to UTC midnight)

## FAQ

### Can I use OAuth instead of API keys?

Yes, for agent and MCP clients. Wisprs supports OAuth 2.0 authorization-code (PKCE) via Clerk with separate least-privilege scopes for transcript reads and writes, operation polling, exports, library reads and writes, search, usage, and AI transforms. Tokens are scope-intersected server-side; unknown scopes are dropped. Discovery lives at \`https://clerk.wisprs.co/.well-known/openid-configuration\` and the resource metadata at \`https://wisprs.co/.well-known/oauth-protected-resource/api/mcp\`. Production rollout is currently gated to reviewed hosts; direct REST integrations should keep using API keys.

### How do I rotate a compromised key?

Go to [Dashboard → API Settings](https://wisprs.co/dashboard/settings/api), revoke the old key, and create a new one with the same scopes. Old keys are invalidated immediately on revocation.

### What happens if I exceed my rate limit?

You receive a \`429 Too Many Requests\` response with a \`Retry-After\` header indicating when you can retry. No requests are dropped silently.
`,
  },
  {
    slug: 'jobs',
    title: 'Async Transcription Jobs: Submit, Poll, and Receive Results',
    description:
      'Submit audio or video URLs for async transcription. Get a job ID immediately, poll for status, or receive a webhook when done.',
    markdown: `# Async Transcription Jobs: Submit, Poll, and Receive Results

Every transcription in Wisprs is an async job. Submit a URL or file, receive a \`jobId\` immediately, then poll for status or configure a webhook to be notified on completion.

## Submit a URL

Send a YouTube link, TikTok URL, podcast feed, or any direct audio/video URL:

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/media/ingest \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://youtube.com/watch?v=dQw4w9WgXcQ",
    "language": "en"
  }'
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "jobId": 42,
    "status": "queued"
  }
}
\`\`\`

## Free caption transcripts (synchronous)

For public YouTube, TikTok, or Instagram videos that already have captions, skip the job pipeline entirely. \`GET /api/v1/transcripts\` returns the caption-derived transcript in one synchronous call — free, no plan minutes, no job created (scope \`transcriptions:read\`):

\`\`\`bash
curl "https://wisprs.co/api/v1/transcripts?url=https://youtube.com/watch?v=dQw4w9WgXcQ&language=en" \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "status": "captions",
    "platform": "youtube",
    "title": "Rick Astley - Never Gonna Give You Up",
    "durationSeconds": 212,
    "language": "en",
    "transcript": "We're no strangers to love…",
    "source": "creator_captions",
    "truncated": false
  }
}
\`\`\`

\`source\` is \`creator_captions\` (uploader-published subtitles), \`auto_captions\` (the platform's ASR track), or \`platform_captions\` (TikTok/Instagram). A \`POST\` twin accepts the same fields as a JSON body. When the video has no captions, the response comes back with \`"status": "no_captions"\` plus guidance: submit the same URL to the billable async pipeline below to transcribe the audio with real speech-to-text.

## Job status lifecycle

Poll \`GET /api/v1/jobs/{id}\` to check progress:

\`\`\`bash
curl https://wisprs.co/api/v1/jobs/42 \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "id": 42,
    "status": "completed",
    "progress": 100,
    "transcriptText": "Welcome to the interview...",
    "failure": null,
    "retryCount": 0,
    "retryable": false,
    "webhooks": { "pending": 0, "delivered": 1, "failed": 0 },
    "updatedAt": "2026-06-17T10:00:14.000Z"
  }
}
\`\`\`

## Status values

- \`pending\` — waiting for intake or a processing slot
- \`processing\` — transcribing your audio
- \`completed\` — transcript is ready in \`transcriptText\`
- \`failed\` — see \`failure.code\` and \`failure.message\`; retry with \`POST /api/v1/jobs/{id}/retry\` (up to three times while the source remains available)

## Retrieve the transcript

\`\`\`bash
curl https://wisprs.co/api/v1/transcriptions/99 \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "id": 99,
    "status": "completed",
    "transcriptText": "Never gonna give you up...",
    "language": "en",
    "duration": 212,
    "speakerCount": 1,
    "createdAt": "2026-06-17T10:00:14.000Z"
  }
}
\`\`\`

## Quality setting

Use \`options.sttQuality\` to trade off speed vs accuracy:

- \`fast\` — lowest latency, suitable for short clips and drafts
- \`balanced\` — default. Good accuracy for most content
- \`accurate\` — highest accuracy, slower. Recommended for dense technical or multilingual content

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/media/ingest \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://youtube.com/watch?v=dQw4w9WgXcQ",
    "language": "en",
    "options": { "sttQuality": "accurate" }
  }'
\`\`\`

## Speaker diarization

Speaker diarization — identifying who said what — is automatically enabled on **Pro plans and above**. No configuration needed: when diarization is available for your plan, the transcript will include speaker labels in the \`json\` and \`md\` export formats.

## Retry a failed job

Failed jobs include a stable client-safe failure code and message plus a \`retryable\` flag. Retry uses retained media when available and supports up to three retry attempts. URL sources are sent back through the appropriate audio download path; they are not passed to the transcription provider as though a webpage were an audio file. Missing source media returns \`422 source_missing\`.

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/jobs/42/retry \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "id": 42,
    "status": "pending"
  }
}
\`\`\`

## SDK usage

\`\`\`typescript
import { WisprsClient } from '@wisprs/sdk';

const client = new WisprsClient({ apiKey: 'wisprs_sk_your_api_key' });

// Submit + wait for completion
const job = await client.transcriptions.create({
  source: { url: 'https://youtube.com/watch?v=dQw4w9WgXcQ' },
});
const result = await client.jobs.wait(job.id); // polls every 2s, 2m timeout
console.log(result.transcriptText);
\`\`\`

## FAQ

### How long do transcription jobs take?

Most jobs complete in a fraction of the audio duration. Short clips (under 5 minutes) typically complete in under a minute. Processing time may be higher during peak load.

### What happens if a job fails?

The job status moves to \`failed\`; the status response includes a safe \`failure.code\` and \`failure.message\`. If \`retryable\` is true, call the retry endpoint. It supports up to three retry attempts while source media is available.

### Can I cancel a job?

Jobs in an active state can be cancelled from the Wisprs dashboard. The public API does not currently expose job cancellation.
`,
  },
  {
    slug: 'webhooks',
    title: 'Webhook Events & Real-Time Job Notifications',
    description:
      'Get notified instantly when transcription jobs complete. HMAC-signed payloads, automatic retries, no polling required.',
    markdown: `# Webhook Events & Real-Time Job Notifications

Webhooks let your app react the moment a transcription job completes or fails — no polling required. Register an endpoint and Wisprs will POST a signed payload to your URL.

## Why webhooks instead of polling?

Polling \`GET /api/v1/jobs/{id}\` works fine for short scripts, but for production systems webhooks are more reliable and efficient. Your server only wakes up when there's something to handle, and you get completion events even if your polling loop restarts or misses a cycle.

## Register an endpoint

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/webhooks/endpoints \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "url": "https://your-app.com/webhooks/wisprs" }'
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "endpoint": {
      "id": 7,
      "url": "https://your-app.com/webhooks/wisprs",
      "signingSecret": "whsec_your_secret",
      "isActive": true
    }
  }
}
\`\`\`

Save the \`signingSecret\` — it's shown once and used to verify signatures.

## Verify the signature

\`\`\`typescript
import { createHmac, timingSafeEqual } from 'node:crypto';

export async function POST(req: Request) {
  const body = await req.text();
  const sig = req.headers.get('x-wisprs-signature') ?? '';
  const expected = createHmac('sha256', process.env.WISPRS_WEBHOOK_SECRET!)
    .update(body)
    .digest('hex');

  if (!timingSafeEqual(Buffer.from(sig), Buffer.from(expected))) {
    return new Response('Forbidden', { status: 403 });
  }

  const event = JSON.parse(body);
  // handle event...
  return new Response('OK');
}
\`\`\`

## Event payload

\`\`\`json
{
  "event": "transcription.completed",
  "data": {
    "transcriptionId": 99,
    "status": "completed",
    "fileName": "interview.mp3",
    "language": "en",
    "duration": 123,
    "completedAt": "2026-06-17T10:00:00.000Z"
  }
}
\`\`\`

## Retry behaviour

Wisprs stores terminal events in a durable outbox and makes an initial attempt plus up to five retries (after 1 minute, 5 minutes, 30 minutes, 2 hours, then 12 hours). Each attempt may make up to three short HTTP attempts. A delivery fails if your endpoint returns a non-2xx status or times out after 10 seconds. Delivery is at least once: deduplicate on event type, transcription ID, and retry count.

## Test a webhook

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/webhooks/test \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "url": "https://your-app.com/webhooks/wisprs" }'
\`\`\`

## FAQ

### Are failed webhook deliveries retried automatically?

Yes. The durable outbox retries up to five times after the initial attempt. Poll \`GET /api/v1/jobs/{id}\` for webhook counts; individual HTTP attempts appear in the endpoint delivery log.

### How do I verify that a webhook came from Wisprs?

Every delivery includes an \`x-wisprs-signature\` header. Compute an HMAC-SHA256 of the raw request body using your signing secret and compare it using a constant-time comparison. The example above shows the full verification pattern.

### Can I receive webhooks for specific events only?

Currently all endpoints receive \`transcription.completed\` and \`transcription.failed\` events. Per-event filtering is on the roadmap.
`,
  },
  {
    slug: 'export',
    title: 'Export Transcripts: SRT, VTT, JSON, Markdown & DOCX',
    description:
      'Export transcripts in 6 formats: TXT, SRT subtitles, WebVTT, timestamped JSON, Markdown, and DOCX.',
    markdown: `# Export Transcripts: SRT, VTT, JSON, Markdown & DOCX

Once a transcription is complete, export it in any format with a single request. Six formats cover subtitle workflows, downstream processing, and content pipelines.

## Endpoint

\`\`\`
GET /api/v1/transcriptions/{id}/export?format={format}
Scope: exports:read
\`\`\`

## Supported formats

| Format | MIME type | Use case |
|---|---|---|
| \`txt\` | text/plain | Raw transcript text, no timestamps |
| \`srt\` | text/plain | SubRip subtitles — YouTube, Premiere, DaVinci |
| \`vtt\` | text/vtt | WebVTT — HTML5 video, Notion, Loom |
| \`json\` | application/json | Timestamped words + segments for custom processing |
| \`md\` | text/markdown | Markdown with speaker labels and timestamps |
| \`docx\` | application/vnd.openxmlformats-officedocument... | Word document for editorial workflows |

## SRT example

\`\`\`bash
curl "https://wisprs.co/api/v1/transcriptions/99/export?format=srt" \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -o subtitles.srt
\`\`\`

Output:
\`\`\`
1
00:00:00,000 --> 00:00:03,240
Never gonna give you up

2
00:00:03,240 --> 00:00:06,480
Never gonna let you down
\`\`\`

## JSON (timestamped words)

The \`json\` format returns segments with word-level timestamps:

\`\`\`json
{
  "transcriptionId": 99,
  "language": "en",
  "durationSeconds": 212,
  "segments": [
    {
      "id": 0,
      "start": 0.0,
      "end": 3.24,
      "text": "Never gonna give you up",
      "speaker": "Speaker 1",
      "words": [
        { "word": "Never", "start": 0.0, "end": 0.4, "probability": 0.99 },
        { "word": "gonna", "start": 0.4, "end": 0.72, "probability": 0.98 }
      ]
    }
  ]
}
\`\`\`

## SDK usage

\`\`\`typescript
const srt = await client.transcriptions.export(99, { format: 'srt' });
// Returns the raw file content as a string

const json = await client.transcriptions.export(99, { format: 'json' });
// Returns parsed object with segments array
\`\`\`

## Notes

- Speaker labels in \`json\` and \`md\` are only available on plans that include speaker diarization (Pro and above). Free-tier transcripts omit speaker fields.
- \`docx\` includes a cover page with the source URL, language, and duration.
- Exports are generated on demand — not pre-stored. Each call re-renders the format from the stored segment data.

## FAQ

### Can I export multiple formats from the same transcript?

Yes. Each export call is independent — you can call the endpoint as many times as you need, with different format values. There's no extra charge per export call.

### Do SRT and VTT files include speaker names?

No. SRT and VTT only contain timecodes and text. Speaker labels are available in the \`json\` and \`md\` formats on Pro plans and above.

### How are word-level timestamps structured in the JSON export?

The \`json\` export returns a \`segments\` array. Each segment has \`start\`, \`end\`, \`text\`, and a nested \`words\` array with per-word start, end, and confidence scores.
`,
  },
  {
    slug: 'repurpose',
    title: 'AI Content Repurposing API: Summaries, Threads, Blog Posts',
    description:
      'Generate summaries, Twitter threads, blog posts, chapter markers, and show notes from any transcript — one API call, multiple output modes.',
    markdown: `# AI Content Repurposing API: Summaries, Threads, Blog Posts

Turn any transcript into summaries, chapter markers, quote collections, Twitter threads, blog posts, or show notes — all via a single endpoint with a mode selector.

The repurpose endpoint takes a completed transcript and generates derivative content in a single API call. Results are returned synchronously — average latency is 3–8 seconds depending on transcript length and mode.

## Endpoint

\`\`\`
POST /api/v1/transcriptions/{id}/repurpose
Scope: repurpose:run
\`\`\`

## Modes

| Mode | What you get | Typical length |
|---|---|---|
| \`summary\` | Paragraph summary of the transcript | 3–5 sentences |
| \`chapters\` | Timestamped chapter markers with titles | 5–15 entries |
| \`quotes\` | Top 5–10 most quotable lines pulled verbatim | 5–10 quotes |
| \`show-notes\` | Podcast show notes with intro, key topics, links placeholder | 200–400 words |
| \`thread\` | Twitter/X thread with hooks and numbered tweets | 8–15 tweets |
| \`blog\` | Full blog post with sections and a CTA | 600–1200 words |

## Example — generate a Twitter thread

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/transcriptions/99/repurpose \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "mode": "thread" }'
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "mode": "thread",
    "content": "1/ Never gonna give you up — breaking down the most misunderstood song of the 80s.\\n\\n2/ Here's what Rick actually meant when he wrote it...",
    "generatedAt": "2026-06-17T10:01:00.000Z"
  }
}
\`\`\`

## Example — chapter markers

\`\`\`bash
curl -X POST https://wisprs.co/api/v1/transcriptions/99/repurpose \\
  -H "Authorization: Bearer wisprs_sk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "mode": "chapters" }'
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "mode": "chapters",
    "content": "00:00 Intro\\n01:22 The hook\\n03:45 Verse breakdown\\n...",
    "chapters": [
      { "title": "Intro", "startSeconds": 0 },
      { "title": "The hook", "startSeconds": 82 }
    ],
    "generatedAt": "2026-06-17T10:01:00.000Z"
  }
}
\`\`\`

## SDK usage

\`\`\`typescript
// Generic repurpose
const result = await client.transcriptions.repurpose(99, 'thread');
console.log(result.content);

// Convenience aliases
const summary  = await client.transcriptions.summary(99);
const chapters = await client.transcriptions.chapters(99);
const quotes   = await client.transcriptions.quotes(99);
\`\`\`

## Notes

- Repurpose calls require the transcript to be in \`completed\` status. A 422 is returned otherwise.
- Results are **not stored** — generate on demand and cache on your end if needed.
- Average latency: ~3–8s depending on transcript length and mode.

## FAQ

### What repurpose modes are available?

Six modes are supported: \`summary\`, \`chapters\`, \`quotes\`, \`show-notes\`, \`thread\`, and \`blog\`. See the modes table above for output format and typical length.

### Can I use my own prompt or tone instructions?

Custom prompt support is on the roadmap. For now, each mode uses a purpose-built prompt optimized for that content type.

### How long does repurposing take?

Most modes return in 3–8 seconds for a typical podcast episode (30–60 minutes). Longer transcripts or the \`blog\` mode (which produces 600–1200 words) may take up to 15 seconds.
`,
  },
  {
    slug: 'search',
    title: 'Semantic Search: Query Your Transcripts by Meaning',
    description:
      'Search your entire transcript library by meaning, not just keywords. Natural language queries, scored results, word-level timestamps.',
    markdown: `# Semantic Search: Query Your Transcripts by Meaning

Search across your entire transcript library by meaning, not just exact keywords. Our search index understands context — find transcripts by topic, speaker, or concept even when the exact words differ.

## Endpoint

\`\`\`
GET /api/v1/library/search?q={query}&limit={n}&minScore={threshold}
Scope: library:search
\`\`\`

## Example

\`\`\`bash
curl "https://wisprs.co/api/v1/library/search?q=product+launch+metrics&limit=5" \\
  -H "Authorization: Bearer wisprs_sk_your_api_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "data": {
    "query": "product launch metrics",
    "results": [
      {
        "transcriptionId": 42,
        "score": 0.91,
        "snippet": "...our MRR hit $12k on launch day, with 340 signups in the first 24 hours...",
        "sourceUrl": "https://youtube.com/watch?v=...",
        "title": "Episode 17: Launch Debrief",
        "timestampSeconds": 892
      },
      {
        "transcriptionId": 17,
        "score": 0.87,
        "snippet": "...the activation rate was 62%, which beat our benchmark by 14 points...",
        "sourceUrl": "https://youtube.com/watch?v=...",
        "title": "Founders Roundtable S2",
        "timestampSeconds": 2341
      }
    ],
    "total": 2
  }
}
\`\`\`

## Parameters

- \`q\` — natural language query (required). No boolean syntax needed.
- \`limit\` — max results (default 10, max 50).
- \`minScore\` — relevance threshold 0–1 (default 0.7). Raise to 0.85+ for tighter, more precise matches.

## SDK usage

\`\`\`typescript
const results = await client.library.search('product launch metrics', {
  limit: 5,
  minScore: 0.8,
});

for (const r of results) {
  console.log(r.transcriptionId, r.score.toFixed(2), r.snippet);
}
\`\`\`

## How it works

- When a transcription completes, it is automatically indexed for semantic search — no extra step needed.
- When you search, your query is matched against all indexed passages owned by your API key by meaning, not by keyword.
- Results are ranked by relevance score and de-duplicated to one snippet per transcription.
- New transcriptions become searchable moments after processing completes.

## FAQ

### How is semantic search different from keyword search?

Keyword search finds exact word matches. Semantic search understands intent — searching for "product revenue" will also surface passages that mention "MRR", "sales figures", or "quarterly earnings" without those words appearing in your query.

### How quickly are transcripts indexed after processing?

Indexing runs as part of the transcription pipeline. By the time a job reaches \`completed\` status, the transcript is already searchable.

### What languages does search support?

Semantic search works across all 100+ languages supported by the transcription API. Query in any language — results will be scored by meaning regardless of the language of the underlying transcript.
`,
  },
  {
    slug: 'sdk-typescript',
    title: 'TypeScript SDK for the Wisprs Transcription API',
    description:
      'Official TypeScript/JavaScript SDK. Type-safe, zero runtime dependencies. Works in Node.js, Bun, Deno, and browser environments.',
    markdown: `# TypeScript SDK for the Wisprs Transcription API

Official TypeScript/JavaScript client for the Wisprs API. Type-safe, zero runtime dependencies, and works in Node.js, Bun, Deno, and browser environments.

The SDK wraps the REST API with typed request and response models, automatic retries, and convenience helpers like \`jobs.wait()\` that eliminate polling boilerplate.

## Install

\`\`\`bash
npm install @wisprs/sdk
# or
yarn add @wisprs/sdk
# or
bun add @wisprs/sdk
\`\`\`

## Initialize

\`\`\`typescript
import { WisprsClient } from '@wisprs/sdk';

const client = new WisprsClient({
  apiKey: process.env.WISPRS_API_KEY!,
  // baseUrl: 'https://wisprs.co', // optional, defaults to production
});
\`\`\`

## Transcriptions

\`\`\`typescript
// Submit a job
const job = await client.transcriptions.create({
  source: { url: 'https://youtube.com/watch?v=...' },
  language: 'en',
  options: { sttQuality: 'balanced' }, // 'fast' | 'balanced' | 'accurate'
});

// Poll until done (2s interval, 2m timeout)
const result = await client.jobs.wait(job.id);
console.log(result.transcriptText);

// Fetch manually
const transcription = await client.transcriptions.get(result.transcriptionId!);
\`\`\`

## Exports

\`\`\`typescript
// Export as SRT
const srt = await client.transcriptions.export(transcriptionId, { format: 'srt' });

// Available: 'txt' | 'srt' | 'vtt' | 'json' | 'md' | 'docx'
\`\`\`

## Repurposing

\`\`\`typescript
const summary  = await client.transcriptions.summary(id);
const chapters = await client.transcriptions.chapters(id);
const quotes   = await client.transcriptions.quotes(id);

// Generic mode: 'summary' | 'chapters' | 'quotes' | 'show-notes' | 'thread' | 'blog'
const thread = await client.transcriptions.repurpose(id, 'thread');
\`\`\`

## Library search

\`\`\`typescript
const results = await client.library.search('launch day metrics', { limit: 5 });
for (const r of results) {
  console.log(r.score.toFixed(2), r.snippet);
}
\`\`\`

## Webhooks

\`\`\`typescript
// Register an endpoint
const endpoint = await client.webhooks.createEndpoint({
  url: 'https://your-app.com/webhooks/wisprs',
});

// List endpoints
const endpoints = await client.webhooks.listEndpoints();

// Check delivery history
const deliveries = await client.webhooks.listDeliveries();

// Retry a failed delivery
await client.webhooks.retryDelivery(deliveryId);
\`\`\`

## Error handling

\`\`\`typescript
import { WisprsApiError } from '@wisprs/sdk';

try {
  const job = await client.transcriptions.create({ source: { url: '...' } });
} catch (err) {
  if (err instanceof WisprsApiError) {
    console.error(err.status, err.message); // e.g. 403, "Missing scope: media:ingest"
  }
}
\`\`\`

## TypeScript types

\`\`\`typescript
import type {
  WisprsJob,
  WisprsTranscription,
  WisprsTranscriptionSegment,
  WisprsExportFormat,
  WisprsRepurposeMode,
  WisprsSearchResult,
} from '@wisprs/sdk';
\`\`\`

## Requirements

- Node.js ≥ 18 (uses native \`fetch\`)
- TypeScript ≥ 5.0
- No external runtime dependencies

## FAQ

### Does it work in the browser?

Yes, with a caveat: never expose your API key in client-side code. Use the SDK server-side and proxy requests through your own backend, or use short-lived scoped tokens if you need browser access.

### Is it ESM or CommonJS?

The package ships both ESM and CJS builds. Your bundler or runtime picks the right one automatically.

### How do I handle API errors?

All API errors throw a \`WisprsApiError\` with \`status\` (HTTP status code) and \`message\` (error string from the API). Wrap calls in a try/catch and check \`err instanceof WisprsApiError\`.
`,
  },
  {
    slug: 'sdk-python',
    title: 'Python SDK for the Wisprs Transcription API',
    description:
      'Official Python client for the Wisprs API. asyncio-native with a sync wrapper. Typed dataclass models. Requires Python ≥ 3.10.',
    markdown: `# Python SDK for the Wisprs Transcription API

Official Python client for the Wisprs API. \`asyncio\`-native with a sync convenience wrapper. All responses are typed dataclass models. Requires Python ≥ 3.10.

Both the sync and async clients share the same method surface — switch between them without rewriting business logic.

## Install

\`\`\`bash
pip install wisprs
# or
uv add wisprs
\`\`\`

## Initialize

\`\`\`python
import os
from wisprs import WisprsClient

client = WisprsClient(api_key=os.environ["WISPRS_API_KEY"])
\`\`\`

## Transcribe a URL (sync)

\`\`\`python
job = client.transcriptions.create(
    source={"url": "https://youtube.com/watch?v=dQw4w9WgXcQ"},
    language="en",
    options={"sttQuality": "balanced"},  # 'fast' | 'balanced' | 'accurate'
)

# Block until done
result = client.jobs.wait(job.id)  # polls every 2s, raises on timeout
print(result.transcript_text)
\`\`\`

## Async usage

\`\`\`python
import asyncio
from wisprs import AsyncWisprsClient

async def main():
    async with AsyncWisprsClient(api_key=os.environ["WISPRS_API_KEY"]) as client:
        job = await client.transcriptions.create(
            source={"url": "https://youtube.com/watch?v=dQw4w9WgXcQ"}
        )
        result = await client.jobs.wait(job.id)
        print(result.transcript_text)

asyncio.run(main())
\`\`\`

## Export

\`\`\`python
srt_content = client.transcriptions.export(transcription_id, format="srt")
with open("subtitles.srt", "w") as f:
    f.write(srt_content)

# JSON export returns a parsed dict
data = client.transcriptions.export(transcription_id, format="json")
\`\`\`

## Repurposing

\`\`\`python
summary  = client.transcriptions.summary(id)
chapters = client.transcriptions.chapters(id)
quotes   = client.transcriptions.quotes(id)

# Generic mode
thread = client.transcriptions.repurpose(id, mode="thread")
print(thread.content)
\`\`\`

## Library search

\`\`\`python
results = client.library.search("product launch metrics", limit=5)
for r in results:
    print(f"{r.score:.2f}  {r.snippet}")
\`\`\`

## Error handling

\`\`\`python
from wisprs.exceptions import WisprsApiError

try:
    job = client.transcriptions.create(source={"url": "..."})
except WisprsApiError as e:
    print(e.status, e.message)  # e.g. 403, "Missing scope: media:ingest"
\`\`\`

## Data models

\`\`\`python
from wisprs.models import (
    Job,
    Transcription,
    TranscriptionSegment,
    SearchResult,
    RepurposeResult,
)

# All fields are typed dataclasses with snake_case attributes
\`\`\`

## FAQ

### What Python version is required?

Python ≥ 3.10. The SDK uses \`match\` statements and newer type union syntax internally.

### Can I use it with Django or FastAPI?

Yes. Use the \`AsyncWisprsClient\` in FastAPI/async Django views, or the sync \`WisprsClient\` in synchronous Django views and management commands.

### How do I handle rate limit errors?

The SDK raises \`WisprsApiError\` on any 4xx/5xx response. Check \`e.status == 429\` to detect rate limits and implement backoff before retrying.
`,
  },
  {
    slug: 'mcp',
    title: 'Wisprs for ChatGPT, Codex, Claude & Claude Code',
    description:
      'Connect an AI host to Wisprs over remote MCP to transcribe, search, export, summarize, repurpose, and translate recordings.',
    markdown: `# Wisprs for ChatGPT, Codex, Claude & Claude Code

Wisprs exposes a secure remote Model Context Protocol endpoint at \`https://wisprs.co/api/mcp\`. Contract v1.8 adds plan-aware text-to-speech voice discovery to the transcript, library, folder, usage, editing, retry, sharing, and private-media handoff surface.

The hosted connector is currently a **private reviewer release**. Public installation links remain unavailable until platform review and Wisprs' release gates are complete.

## Public v1 tools

| Tool | Result | Required scope |
|---|---|---|
| \`transcribe_url\` | Returns a durable \`op_…\` receipt immediately | \`transcripts:write\` |
| \`get_video_transcript\` | Returns the existing caption track of a public YouTube, TikTok, or Instagram video as text (no speech-to-text, no minutes used), or \`no_captions\` | \`transcripts:read\` |
| \`get_private_media_upload_handoff\` | Returns the authenticated Wisprs uploader without exposing file bytes or upload tokens to the model | \`transcripts:write\` |
| \`list_tts_voices\` | Returns a bounded plan-filtered voice catalog with opaque handles | \`tts:read\` |
| \`synthesize_speech\` | Starts a metered text-to-speech job and returns a durable receipt | \`tts:write\` |
| \`list_tts_syntheses\` | Returns bounded text-to-speech history with short untrusted previews | \`tts:read\` |
| \`get_tts_audio\` | Returns a short-lived private download URL for a completed synthesis | \`tts:read\` |
| \`list_meeting_sessions\` | Returns bounded meeting capture status with consent state | \`meetings:read\` |
| \`schedule_meeting_capture\` | Schedules a meeting agent after explicit recording consent | \`meetings:write\` |
| \`cancel_meeting_capture\` | Cancels a not-yet-dispatched meeting agent after confirmation | \`meetings:write\` |
| \`list_support_tickets\` | Returns the connected user's own support tickets with bounded previews | \`support:read\` |
| \`create_support_ticket\` | Creates a bounded support request for the authenticated user | \`support:write\` |
| \`list_webhook_endpoints\` | Returns webhook endpoints with signing secrets redacted | \`webhooks:read\` |
| \`create_webhook_endpoint\` | Creates an HTTPS webhook endpoint after SSRF-safe DNS validation | \`webhooks:write\` |
| \`update_webhook_endpoint\` | Updates a tenant-owned webhook URL or active state | \`webhooks:write\` |
| \`delete_webhook_endpoint\` | After confirmation, reversibly disables a webhook endpoint | \`webhooks:write\` |
| \`list_organization_members\` | Returns workspace members without private account fields | \`organization:read\` |
| \`list_creator_packs\` | Returns the bounded creator packs available to the account | \`creator:read\` |
| \`list_creator_templates\` | Returns bounded creator workflow templates and their outcomes | \`templates:read\` |
| \`list_notifications\` | Returns bounded in-app notifications for the authenticated user | \`notifications:read\` |
| \`mark_notifications_read\` | Marks selected or all unread notifications as read | \`notifications:write\` |
| \`list_operations\` | Returns bounded operation history without payloads or source URLs | \`operations:read\` |
| \`get_job_status\` | Returns queued, running, completed, failed, or canceled state | \`operations:read\` |
| \`get_transcript\` | Returns bounded transcript pages from a \`tr_…\` handle | \`transcripts:read\` |
| \`get_transcript_artifact\` | Returns bounded pages of generated or translated content | \`ai:transform\` |
| \`search_library\` | Returns bounded ranked snippets from the connected tenant | \`library:search\` |
| \`list_transcripts\` | Returns one signed-keyset-paginated library page | \`transcripts:read\` |
| \`get_transcription_metadata\` | Returns bounded metadata without transcript text | \`transcripts:read\` |
| \`list_folders\` | Returns tenant-owned folders and live media counts | \`library:read\` |
| \`get_usage_and_limits\` | Returns plan usage, limits, and reset windows without billing secrets | \`usage:read\` |
| \`create_folder\` | Creates a tenant-owned media folder through a durable operation | \`library:write\` |
| \`rename_folder\` | Renames a tenant-owned folder without changing its contents | \`library:write\` |
| \`move_transcript_to_folder\` | Files or unfiles one tenant-owned transcript | \`library:write\` |
| \`delete_folder\` | After explicit confirmation, soft-deletes a folder and preserves its media | \`library:write\` |
| \`rename_transcript\` | Renames a transcript only when its expected revision is current | \`transcripts:write\` |
| \`edit_transcript_text\` | Applies a confirmed bounded literal edit with a revision guard | \`transcripts:write\` |
| \`retry_transcription\` | Requeues a failed transcript from retained source media after confirmation | \`transcripts:write\` |
| \`create_share_link\` | Creates an explicitly confirmed, expiring public transcript link | \`sharing:write\` |
| \`revoke_share_link\` | Revokes a public transcript link through an idempotent operation | \`sharing:write\` |
| \`export_transcript\` | Returns a private one-hour MCP resource for six export formats | \`transcripts:export\` |
| \`summarize_transcript\` | Starts durable summary generation | \`ai:transform\` |
| \`generate_chapters\` | Starts durable chapter generation | \`ai:transform\` |
| \`repurpose_transcript\` | Starts show-notes, thread, blog, or quote generation | \`ai:transform\` |
| \`translate_transcript\` | Starts a language-specific translation | \`ai:transform\` |

## ChatGPT and Codex

The OpenAI distribution is a universal plugin containing a registered remote MCP connection and provider-neutral operating instructions. During private review, use the reviewer package and account supplied by Wisprs. After approval, this section will link to the public directory listing.

The host performs OAuth discovery against Wisprs, requests only the scopes needed for the selected outcome, and calls the same tools above. Do not enter a Wisprs API key into a ChatGPT conversation.

## Claude and Claude Code

The Anthropic distribution contains a Claude plugin manifest plus this remote MCP definition:

\`\`\`json
{
  "mcpServers": {
    "wisprs": {
      "type": "http",
      "url": "https://wisprs.co/api/mcp"
    }
  }
}
\`\`\`

During private review, install only the signed package or marketplace source supplied by Wisprs. After Anthropic approval, this section will link to the public connector and plugin listings.

## The durable workflow

1. Ask the host to transcribe a public HTTPS media URL.
2. Keep the returned \`op_…\` receipt. It is safe to retry the original call with the same idempotency key.
3. Call \`get_job_status\` later. Do not resubmit media merely because processing is still running.
4. When the operation succeeds, use its opaque \`tr_…\` handle with \`get_transcript\`.
5. Follow the returned cursor until \`nextCursor\` is null.
6. For generated content, call \`get_transcript_artifact\` with the completed result metadata. For exports, read the exact returned \`wisprs://exports/res_…\` resource before it expires.

Example prompts:

\`\`\`text
Transcribe this public recording and tell me when it is ready: https://example.com/recording.mp3
Check operation op_… with get_job_status and report its current state.
Retrieve transcript tr_… page by page and preserve source order.
Find where we discussed pricing in my Wisprs library.
Summarize transcript tr_…, then retrieve the generated artifact.
Export transcript tr_… as DOCX without placing the file contents in chat.
\`\`\`

## Security and tenancy

- Remote host connections use OAuth; \`transcripts:write\` and \`transcripts:read\` are separate grants.
- Transcript and operation identifiers are opaque. A valid handle never bypasses tenant authorization.
- Media intake accepts public HTTPS destinations only. Private networks, unsafe redirects, unsupported media, and oversized responses are rejected.
- Revoked or expired grants fail closed.
- Channel telemetry excludes prompts, transcript text, media URLs, filenames, access tokens, email addresses, and raw IP addresses.

## Developer stdio connection

The existing \`@wisprs/mcp\` npm package is the developer-controlled alternative for stdio hosts. It uses a Wisprs API key and is separate from the hosted OAuth directory listings.

\`\`\`bash
WISPRS_API_KEY=wisprs_sk_your_api_key npx @wisprs/mcp
\`\`\`

Example stdio configuration:

\`\`\`json
{
  "mcpServers": {
    "wisprs": {
      "command": "npx",
      "args": ["@wisprs/mcp"],
      "env": { "WISPRS_API_KEY": "wisprs_sk_your_api_key" }
    }
  }
}
\`\`\`

Keep the key in the host's secret or environment configuration, never in a prompt or committed config file. Scope the key to the operations the local package needs.

## Troubleshooting

### No tools appear

Reconnect, confirm that OAuth completed, and start a new conversation so the host refreshes its tool list. Reviewer access also requires the host client or account to be in the active rollout cohort.

### The media URL is rejected

Use a public HTTPS URL that resolves directly to supported media. URLs resolving to private or local addresses, redirects to unsafe destinations, and responses above the intake limit are blocked.

### The operation is still running

Long media is asynchronous by design. Preserve the \`op_…\` receipt and check it later; do not create a duplicate operation.

### Access was revoked

Reconnect through the host. A revoked grant cannot read prior operation or transcript handles without fresh, valid authorization.
`,
  },
];

export const DOCS_SLUG_SET = new Set(DOCS_CONTENT.map((d) => d.slug));

export function getDoc(slug: string): DocEntry | undefined {
  return DOCS_CONTENT.find((d) => d.slug === slug);
}
